import { Monster } from "./monster.js";

export class huntingHandler {
  constructor(gameState) {
    this.gameState = gameState;
    this.activeMonster = null; // Only track the monster currently engaged

    this.balance = {
      baseMonsterHealth: 10
    };

    this.equippedHotbar = [];
    this.isAutoHunting = false;

    this.combatQueue = {
      isActive: false,
      spells: [],
      currentIndex: 0,
      progress: 0,
      targetDuration: 0,
      currentElement: null
    };
  }

  // Spawns or returns the monster on the player's current tile
  getCurrentMonster() {
    const tile = this.gameState.currentTile;
    if (!tile || tile === "empty") {
      this.activeMonster = null;
      return null;
    }

    if (!this.activeMonster) {
      this.activeMonster = this.generateMonsterForTile(tile);
    }

    return this.activeMonster;
  }

  // Call this whenever the player moves to a new tile
  onTileChanged() {
    this.activeMonster = null;
    this.getCurrentMonster();
  }

  generateMonsterForTile(tile) {
    const element = tile.stats?.element || "Fire";
    const tier = tile.stats?.tier || 1;
    const uniqueId = `monster_${tile.xPos}_${tile.yPos}_${Date.now()}`;

    return new Monster(uniqueId, element, tier, this.balance.baseMonsterHealth);
  }

  getMonsterElement() {
    const monster = this.getCurrentMonster();
    return monster ? monster.element : (this.gameState.currentTile?.stats?.element || "Unknown");
  }

  getMonsterMaxHp() {
    const monster = this.getCurrentMonster();
    if (monster) return monster.maxHp;

    const tile = this.gameState.currentTile;
    if (!tile || tile === "empty") return 0;
    const tier = tile.stats?.strengthLevel || 1;
    return this.balance.baseMonsterHealth * tier;
  }

  getMonsterCurrentHp() {
    const monster = this.getCurrentMonster();
    return monster ? monster.currentHp : this.getMonsterMaxHp();
  }

  getMonsterHealthPercentage() {
    const max = this.getMonsterMaxHp();
    if (max <= 0) return 0;
    return (this.getMonsterCurrentHp() / max) * 100;
  }

  executeHuntSequence(elementsQueue) {
    const monster = this.getCurrentMonster();
    if (!monster || monster.isDead) return;

    this.equippedHotbar = [...elementsQueue];
    this.combatQueue.spells = [...elementsQueue];
    this.combatQueue.currentIndex = 0;

    this.loadNextSpellInQueue();
  }

  loadNextSpellInQueue() {
    if (this.combatQueue.currentIndex >= this.combatQueue.spells.length) {
      if (this.isAutoHunting && this.combatQueue.spells.length > 0) {
        this.combatQueue.currentIndex = 0;
      } else {
        this.combatQueue.isActive = false;
        return;
      }
    }

    const elementName = this.combatQueue.spells[this.combatQueue.currentIndex];

    let calculatedSpeed = 1;
    if (this.gameState.spellHandler?.calculateSpellStats) {
      const liveStats = this.gameState.spellHandler.calculateSpellStats(elementName);
      if (liveStats?.totalSpeed) {
        calculatedSpeed = liveStats.totalSpeed;
      }
    }

    this.combatQueue.currentElement = elementName;
    this.combatQueue.targetDuration = calculatedSpeed * 1000;
    this.combatQueue.progress = 0;
    this.combatQueue.isActive = true;
  }

  castSpellAtCurrentMonster(spell) {
    const monster = this.getCurrentMonster();
    if (!monster || monster.isDead) return { success: false, status: "no_target" };

    if (spell.stats.mpCost > 1000) {
      const hasEnoughMP = this.gameState.deductPlayerMP(spell.stats.mpCost);
      if (!hasEnoughMP) return { success: false, status: "insufficient_mp" };
    }

    const damage = spell.stats.basePower;
    const combatResult = monster.takeDamage(damage);

    if (combatResult.killed) {
      this.gameState.uiInfo.questProgressUpdate = true;

      // Update quest progress in the active world
      if (this.gameState.currentTile?.currentQuest) {
        this.gameState.worldHandler.getActiveWorld()?.killMonster(this.gameState.currentTile);
      }

      if (this.gameState.soulHandler) {
        this.gameState.soulHandler.addMonsterSouls(monster);
        this.gameState.uiInfo.soulUpdate = true;
      }
      // Respawn a fresh monster on the current tile immediately
      const tile = this.gameState.currentTile;
      this.activeMonster = tile && tile !== "empty" ? this.generateMonsterForTile(tile) : null;
    }

    this.gameState.uiInfo.updateHuntingSubmenu = true;
    return { success: true, status: combatResult.killed ? "dead" : "alive", damageDealt: damage };
  }

  // --- SAVE / LOAD (Only persist player loadout, zero monster state) ---

  saveToData() {
    return {
      hotbar: this.equippedHotbar,
      isAutoHunting: this.isAutoHunting
    };
  }

  loadFromData(data) {
    this.activeMonster = null;
    this.equippedHotbar = [];

    if (!data) return;

    if (data.hotbar) {
      this.equippedHotbar = [...data.hotbar];
    }
    if (typeof data.isAutoHunting === "boolean") {
      this.isAutoHunting = data.isAutoHunting;
      console.log(this.isAutoHunting);
      if (this.isAutoHunting && this.equippedHotbar.length > 0) {
        this.executeHuntSequence(this.equippedHotbar);
      }
    }
  }

  toJSON() {
    return undefined;
  }

  update(dt) {
    if (!this.combatQueue.isActive) return;

    this.combatQueue.progress += dt;

    if (this.combatQueue.progress >= this.combatQueue.targetDuration) {
      const elementName = this.combatQueue.currentElement;

      let calculatedPower = 0;
      let calculatedCost = 0;
      if (this.gameState.spellHandler?.calculateSpellStats) {
        const liveStats = this.gameState.spellHandler.calculateSpellStats(elementName);
        
        if (liveStats) {
            if (liveStats.shardCount == 0) {
                this.combatQueue.isActive = false;
                return;
            }
          calculatedPower = liveStats.totalPower || 0;
          calculatedCost = liveStats.totalMPCost || 0;
        }
      }

      const spellPayload = {
        elementName: elementName,
        stats: { basePower: calculatedPower, mpCost: calculatedCost }
      };

      const combatResult = this.castSpellAtCurrentMonster(spellPayload);

      if (!combatResult.success) {
        this.combatQueue.isActive = false;
        this.isAutoHunting = false;
        this.gameState.uiInfo.updateHuntingSubmenu = true;
        return;
      }

      this.combatQueue.currentIndex++;
      this.loadNextSpellInQueue();
    }
  }
}